/**
 * Groups an array of items into an object based on a key selector function.
 *
 * @template T The type of elements in the input array.
 * @param {T[]} array The array to be grouped.
 * @param {Function} keySelector A function that returns the grouping key for each item.
 * @returns {Record<string, T[]>} An object where keys are the grouping keys and values are arrays of matching items.
 *
 * @example
 * const people = [
 *   { name: 'Alice', age: 30 },
 *   { name: 'Bob', age: 25 },
 *   { name: 'Charlie', age: 30 }
 * ];
 * const groupedByAge = arrayGroupBy(people, person => person.age.toString());
 * // Result:
 * // {
 * //   '25': [{ name: 'Bob', age: 25 }],
 * //   '30': [{ name: 'Alice', age: 30 }, { name: 'Charlie', age: 30 }]
 * // }
 *
 * @remarks
 * - If multiple items produce the same key, they will be grouped into an array under that key.
 * - This function does not modify the original array.
 */
export const arrayGroupBy = <T>(
    array: T[],
    keySelector: (item: T) => string,
): Record<string, T[]> =>
    array.reduce(
        (result, currentValue) => {
            const key = keySelector(currentValue);
            (result[key] = result[key] || []).push(currentValue);

            return result;
        },
        {} as Record<string, T[]>,
    );
